import styled, { keyframes } from "styled-components";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { transform: translateX(-20px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const typingAnimation = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-5px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
`;

export const Container = styled.div`
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
  animation: ${fadeIn} 0.6s ease-out;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

export const Header = styled.div`
  background: linear-gradient(135deg, #1a1a1a 0%, #333 100%);
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid #444;
`;

export const HeaderTitle = styled.h1`
  color: #ff6b35;
  font-size: 2rem;
  font-weight: bold;
  margin: 0 0 1.5rem 0;
  flex-wrap: wrap;
  display: flex;
  align-items: center;
  gap: 1rem;
  @media (max-width: 768px) {
    justify-content: center;
    text-align: center;
  }
`;

export const HeaderStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
`;

export const StatCard = styled.div`
  background: rgba(255, 107, 53, 0.1);
  border: 1px solid rgba(255, 107, 53, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 53, 0.15);
    transform: translateY(-2px);
  }
`;

export const StatNumber = styled.div`
  font-size: 2.5rem;
  font-weight: bold;
  color: #ff6b35;
  margin-bottom: 0.5rem;
`;

export const StatLabel = styled.div`
  color: #cccccc;
  font-size: 0.9rem;
  font-weight: 500;
`;

export const MainContent = styled.div`
  display: grid;
  grid-template-columns: 400px 1fr;
  gap: 2rem;
  height: 100%;

  min-height: calc(100dvh - 57px - 286.23px - 2rem - 20px - 20px);

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
    height: auto;
  }
`;

export const ChatList = styled.div`
  background: #1a1a1a;
  border-radius: 15px;
  border: 1px solid #333;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const ChatSessionList = styled.div`
  overflow: auto;
  display: flex;
  flex-direction: column;
  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: #333;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ff6b35;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #e55a2b;
  }
`;

export const ChatListHeader = styled.div`
  background: rgba(255, 107, 53, 0.1);
  padding: 1.5rem;
  border-bottom: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h3 {
    color: #ff6b35;
    margin: 0;
    font-size: 1.2rem;
  }
`;

export const ChatItem = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #333;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  animation: ${slideIn} 0.3s ease-out;
  border-left: 3px solid
    ${(props) => (props.$active ? "#ff6b35" : "transparent")};
  background: ${(props) =>
    props.$active
      ? "rgba(255, 107, 53, 0.1)"
      : props.$unread
      ? "rgba(255, 107, 53, 0.05)"
      : "transparent"};

  &:hover {
    background: rgba(255, 107, 53, 0.1);
    color: #ff6b35;
    border-left-color: #ff6b35;
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const ChatPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

export const ChatUser = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  .avatar {
    width: 40px;
    height: 40px;
    background: #333;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ff6b35;
    font-size: 1.2rem;
  }

  .info {
    flex: 1;
  }

  .band-name {
    color: #888;
    font-size: 0.8rem;
    margin-top: 0.2rem;
  }
`;

export const ChatUserName = styled.div`
  color: #ffffff;
  font-weight: bold;
  font-size: 1rem;
`;

export const ChatUserEmail = styled.div`
  color: #888;
  font-size: 0.8rem;
`;

export const ChatLastMessage = styled.div`
  color: #cccccc;
  font-size: 0.9rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const ChatTime = styled.div`
  color: #888;
  font-size: 0.8rem;
  text-align: right;
`;

export const ChatStatus = styled.div`
  position: absolute;
  top: 1rem;
  right: 14.5px;
  background: #ff6b35;
  color: white;
  border-radius: 50%;
  width: 15px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: bold;
`;

export const OnlineIndicator = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(props) => (props.$online ? "#27ae60" : "#888")};
  margin-left: auto;
  animation: ${(props) => (props.$online ? pulse : "none")} 2s infinite;
`;

export const TypingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #ff6b35;
  font-style: italic;

  background: #333;

  padding: 1rem 1.2rem;
  border-radius: 15px 15px 15px 5px;

  line-height: 1.4;
  width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  .dots {
    display: flex;
    gap: 2px;

    span {
      width: 5px;
      height: 5px;
      background: #ff6b35;
      border-radius: 50%;
      animation: ${typingAnimation} 1.4s infinite;

      &:nth-child(2) {
        animation-delay: 0.2s;
      }

      &:nth-child(3) {
        animation-delay: 0.4s;
      }
    }
  }
`;

export const ChatArea = styled.div`
  background: #1a1a1a;
  border-radius: 15px;
  border: 1px solid #333;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-width: 50rem;
`;

export const ChatHeader = styled.div`
  background: rgba(255, 107, 53, 0.1);
  padding: 1.5rem;
  border-bottom: 1px solid #333;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

export const ChatUserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  .avatar {
    width: 50px;
    height: 50px;
    background: #333;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ff6b35;
    font-size: 1.5rem;
  }
`;

export const ChatActions = styled.div`
  display: flex;
  gap: 0.5rem;
  position: relative;
`;

export const ActionButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid #555;
  color: #cccccc;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 107, 53, 0.2);
    color: #ff6b35;
    border-color: #ff6b35;
  }
`;

export const ChatDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: #2c2c2c;
  border: 1px solid #555;
  border-radius: 8px;
  min-width: 150px;
  z-index: 1000;
  margin-top: 0.5rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
`;

export const DropdownItem = styled.div`
  padding: 0.8rem 1rem;
  color: #cccccc;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    background: rgba(255, 107, 53, 0.1);
    color: #ff6b35;
  }

  &:first-child {
    border-radius: 8px 8px 0 0;
  }

  &:last-child {
    border-radius: 0 0 8px 8px;
  }
`;

export const MessagesContainer = styled.div`
  flex: 1;
  padding: 1.5rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 30rem;
  max-width: 50rem;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #333;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ff6b35;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #e55a2b;
  }
`;

export const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$isAdmin ? "flex-end" : "flex-start")};
  animation: ${fadeIn} 0.3s ease-out;
`;

export const MessageSender = styled.div`
  font-size: 0.8rem;
  color: #888;
  margin-bottom: 0.3rem;
`;

export const MessageContent = styled.div`
  background: ${(props) =>
    props.$isAdmin
      ? "linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%)"
      : "#333"};
  color: white;
  padding: 1rem 1.2rem;
  border-radius: ${(props) =>
    props.$isAdmin ? "15px 15px 5px 15px" : "15px 15px 15px 5px"};
  max-width: 70%;
  word-wrap: break-word;
  line-height: 1.4;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

export const MessageTime = styled.div`
  font-size: 0.7rem;
  color: #888;
  margin-top: 0.3rem;
`;

export const QuickResponses = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid #333;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const QuickResponseButton = styled.button`
  background: rgba(255, 107, 53, 0.1);
  border: 1px solid rgba(255, 107, 53, 0.3);
  color: #ff6b35;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  &:hover {
    background: rgba(255, 107, 53, 0.2);
    transform: translateY(-1px);
  }
`;

export const InputContainer = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #333;
  display: flex;
  gap: 1rem;
  align-items: center;
`;

export const ChatInput = styled.input`
  flex: 1;
  background: #333;
  border: 1px solid #555;
  border-radius: 12px;
  padding: 10px 20px;
  color: white;
  font-family: "Poppins-Regular";
  font-size: 0.9rem;

  height: 40px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #ff6b35;
  }

  &::placeholder {
    color: #888;
  }
`;

export const SendButton = styled.button`
  background: ${(props) =>
    props.disabled
      ? "#555"
      : "linear-gradient(135deg, #ff6b35 0%, #e55a2b 100%)"};
  border: none;
  color: white;
  width: 40px;
  height: 40px;

  border-radius: 12px;

  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.3s ease;
  font-size: 1rem;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #e55a2b 0%, #d44a1f 100%);
    transform: scale(1.05);
  }

  &:disabled {
    opacity: 0.5;
  }
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  gap: 0.5rem;
  margin-top: 2rem;
  padding: 2rem;
`;

export const EmptyStateIcon = styled.div`
  font-size: 4rem;
  color: #444;
  margin-bottom: 1rem;
`;

export const EmptyStateText = styled.div`
  font-size: 1.1rem;
  text-align: center;
  line-height: 1.6;
`;
